import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoreHorizontal, UserX, UserCheck, Trash2, Baby } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { SPACE_COLORS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/Avatars";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_ORDER = { owner: 4, admin: 3, member: 2, guest: 1 };

const ManagedMemberDialog = ({ ws, onDone }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(SPACE_COLORS[3]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      await api.post(`/workspaces/${ws.id}/members/managed`, { name, avatar_color: color });
      toast.success(`${name} añadido al Espacio`);
      onDone();
      setOpen(false);
      setName("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="managed-member-create-btn">
          <Baby className="h-4 w-4 mr-1" /> Añadir miembro sin email
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="managed-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Miembro sin cuenta</DialogTitle>
          <DialogDescription>
            Para peques u otros miembros del núcleo familiar sin email. No pueden iniciar sesión, pero se les pueden asignar tareas, recompensas y gastos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input data-testid="managed-member-name-input" value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Ej: Lucía" maxLength={100} autoFocus />
          </div>
          <div className="space-y-2">
            <Label>Color del avatar</Label>
            <div className="flex flex-wrap gap-2">
              {SPACE_COLORS.map((c) => (
                <button key={c} data-testid={`managed-member-color-${c.replace("#", "")}`} onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-primary scale-110" : ""}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <Button className="w-full" onClick={save} disabled={saving} data-testid="managed-member-save-btn">
            {saving ? "Guardando…" : "Añadir al Espacio"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MembersTab = ({ ws, members }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const myRole = ws.my_role;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["members", ws.id] });
    qc.invalidateQueries({ queryKey: ["audit", ws.id] });
  };

  const canManage = (m) =>
    m.role !== "owner" && m.profile_id !== user?.id &&
    (ROLE_ORDER[myRole] > ROLE_ORDER[m.role] || (m.managed && ROLE_ORDER[myRole] >= ROLE_ORDER.admin));

  const updateMember = async (memberId, payload) => {
    try {
      await api.put(`/workspaces/${ws.id}/members/${memberId}`, payload);
      invalidate();
      toast.success("Miembro actualizado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/workspaces/${ws.id}/members/${memberId}`);
      invalidate();
      qc.invalidateQueries({ queryKey: ["workspace", ws.id] });
      toast.success("Miembro eliminado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="settings-members-tab">
      {ROLE_ORDER[myRole] >= ROLE_ORDER.admin && (
        <div className="flex justify-end">
          <ManagedMemberDialog ws={ws} onDone={invalidate} />
        </div>
      )}
      <Card className="divide-y divide-border">
      {members.map((m) => (
        <div key={m.id} className="p-4 flex items-center gap-3" data-testid={`member-row-${m.id}`}>
          <UserAvatar name={m.name} color={m.avatar_color} src={m.avatar_url} size="md" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {m.name} {m.profile_id === user?.id && <span className="text-muted-foreground">(tú)</span>}
            </p>
            <p className="text-xs text-muted-foreground truncate">{m.managed ? "Sin cuenta · no puede iniciar sesión" : m.email}</p>
          </div>
          {m.managed && (
            <Badge variant="outline" className="text-xs shrink-0 gap-1" data-testid={`member-managed-badge-${m.id}`}>
              <Baby className="h-3 w-3" /> Sin email
            </Badge>
          )}
          {m.status === "suspended" && <Badge variant="destructive" className="text-xs shrink-0">Suspendido</Badge>}
          {m.role === "owner" ? (
            <Badge className="shrink-0">{m.role_label}</Badge>
          ) : canManage(m) ? (
            <Select value={m.role} onValueChange={(role) => updateMember(m.id, { role })}>
              <SelectTrigger className="w-36 h-8 text-xs shrink-0" data-testid={`member-role-select-${m.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{ws.role_labels?.admin || "Administrador"}</SelectItem>
                <SelectItem value="member">{ws.role_labels?.member || "Miembro"}</SelectItem>
                <SelectItem value="guest">{ws.role_labels?.guest || "Invitado"}</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="secondary" className="shrink-0">{m.role_label}</Badge>
          )}
          {canManage(m) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`member-actions-btn-${m.id}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {m.status === "active" ? (
                  <DropdownMenuItem data-testid={`member-suspend-btn-${m.id}`}
                    onClick={() => updateMember(m.id, { status: "suspended" })}>
                    <UserX className="h-4 w-4 mr-2" /> Suspender
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid={`member-activate-btn-${m.id}`}
                    onClick={() => updateMember(m.id, { status: "active" })}>
                    <UserCheck className="h-4 w-4 mr-2" /> Reactivar
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid={`member-remove-btn-${m.id}`} className="text-destructive"
                  onClick={() => removeMember(m.id)}>
                  <Trash2 className="h-4 w-4 mr-2" /> Eliminar del Espacio
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
      </Card>
    </div>
  );
};
